import 'rc-slider/assets/index.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './RecordingOwnAudio.module.css';
import { Button, Flex, Input, message, Modal, Tooltip } from 'antd';
import {
  calculatePlayerTimeSeek,
  concatTwoAudioBuffers,
  formatSeconds,
  getAudioBufferFromBlob,
  getAudioDuration,
  gotMicPermissions,
  handleErrorMessage,
  handleInputChange,
} from '../../utils';
import backIcon from '../../../studio/assets/recordingAudio/backIcon.png';
import closeIcon from '../../../studio/assets/recordingAudio/closeIcon.png';
import pauseRecodingIcon from '../../../studio/assets/recordingAudio/pauseRecodingIcon.png';
import keepIcon from '../../../studio/assets/recordingAudio/keepIcon.png';
import deleteIcon from '../../../studio/assets/recordingAudio/deleteIcon.png';
import playIcon from '../../../studio/assets/recordingAudio/playIcon.png';
import playInPlayerIcon from '../../../studio/assets/recordingAudio/playInPlayerIcon.png';
import forwardIcon from '../../../studio/assets/recordingAudio/forwardIcon.png';
import backwardIcon from '../../../studio/assets/recordingAudio/backwardIcon.png';
import pauseIcon from '../../../studio/assets/recordingAudio/pauseIcon.png';
import microphone from '../../../studio/assets/recordingAudio/microphone.png';
import { RecordingAudioState } from '../../../types';
import { useReactMediaRecorder } from 'react-media-recorder-2';
import useAudioUpload from './useAudioUpload';
import ReactPlayer from 'react-player';
import Slider from 'rc-slider';
import toWav from 'audiobuffer-to-wav';
import { batch, useDispatch, useSelector } from 'react-redux';
import { IState } from '../../../graphql/types';
import useGiideAudioLibrary from '../../../hooks/useQuery/useGiideAudioLibrary';
import { AudioContext } from 'standardized-audio-context';
import confirm from 'antd/es/modal/confirm';
import { detect } from 'detect-browser';
import {
  setGetStartedModalOpen,
  setGiideTabState,
  setOwnRecordingStarted,
  setProcessingRecording,
  setRecordingProcessingModalVisible,
} from '../../../actions';
import useUser from '../../../hooks/useUser';
import { copySampleAudio } from '../../../graphql/mutations';
import useMutation from '../../../hooks/useMutation';
import useGiide from '../../../hooks/useQuery/useGiide';
import {
  CopySampleAudioInput,
  CopySampleAudioMutation,
} from '../../../graphql/api';

const fallbackErrorMessage = 'Failed to process the audio.';

let timerInterval;
let countDownInterval;
const tooltipText = () => {
  return (
    <div className={styles.recordingInfoTooltipWrapper}>
      <div>Tips for recording audio</div>
      <ul>
        <li>
          While speaking off-the-cuff is totally fine, most people find it
          helpful to write down some notes or a script before recording.
        </li>
        <li>
          Practicing helps with stage fright! If you’re feeling tongue-tied, try
          making a giide about your favorite restaurant or last vacation.
        </li>
        <li>
          If you make a mistake while recording, keep going! Think of this as a
          conversation rather than a presentation, and let your personality come
          through.
        </li>
        <li>
          Feel free to tell your audience how to interact with the content
          you’ve shared and when to pause. You’re their giide through a new
          topic.
        </li>
        <li>
          If you’d like help from a Giide pro,{' '}
          <a href="mailto:support@giide.com">contact us</a>.
        </li>
      </ul>
    </div>
  );
};
const browser = detect();

const useRecordingOwnAudio = (giideId: string) => {
  const dispatch = useDispatch();
  const maxGiideLengthInSecounds = useSelector<IState, number>(state =>
    state.user?.plan?.features?.giideMaxLengthAudio?.giideLengthInSeconds
      ? state.user?.plan?.features?.giideMaxLengthAudio?.giideLengthInSeconds
      : 0
  );
  const { handleUpload } = useAudioUpload();
  const [recordingAudioState, setRecordingAudioState] =
    useState<RecordingAudioState>('info');
  const [recordedDataBlob, setRecordedDataBlob] = useState<
    BlobPart | undefined
  >(undefined);
  const [recordedDataUrl, setRecordedDataUrl] = useState<string | undefined>(
    undefined
  );
  const [audioFileName, setAudioFileName] = useState<string>('');
  const [timer, setTimer] = useState<number>(maxGiideLengthInSecounds); // in seconds
  const [totalDuration, setTotalDuration] = useState<number>(0); // in seconds
  const [countDown, setCountDown] = useState<number>(3); // in seconds
  const [backFromPreview, setBackFromPreview] = useState<boolean>(false);
  // const [testingMicBtnText, setTestingMicBtnText] =
  //   useState<string>('Test mic');
  // const [testAudioPlaying, setTestAudioPlaying] = useState<boolean>(false);
  const playerRef = React.createRef<ReactPlayer>();
  const [playing, setPlaying] = useState<boolean>(false);
  const [progressTime, setProgressTime] = useState(0);
  const [recAudioBuffers, setRecAudioBuffers] = useState<AudioBuffer[]>([]);
  const [playerOpened, setPlayerOpened] = useState<boolean>(false);
  const [exceededMaxDuration, setExceededMaxDuration] =
    useState<boolean>(false);
  const { audiopLibraryItemsCount } = useGiideAudioLibrary();
  const [testedAudio, setTestedAudio] = useState<boolean>(false);
  const [clearAudioData, setClearAudioData] = useState<boolean>(false);
  const [exitFromRecording, setExitFromRecording] = useState<boolean>(false);
  const [infoTooltipVisible, setInfoTooltipVisible] = useState<boolean>(false);
  const infoTooltipTextRef = useRef<HTMLSpanElement>(null);
  const ownAudioRecordingRef = React.useRef<HTMLDivElement>(null);
  const [exitConfirmationModalVisible, setExitConfirmationModalVisible] =
    useState<boolean>(false);
  const [browserName, setBrowserName] = useState<string | undefined>(
    browser?.name
  );
  const [showAudioLibrary, setShowAudioLibrary] = useState<boolean>(false);

  const { userHasAllowedRecording } = useUser();
  const [isRecordingAllowed, setIsRecordingAllowed] = useState<boolean>(true);

  useEffect(() => {
    let browserMainVersion;
    let browserName;
    if (browser) {
      browserName = browser.name;
      browserMainVersion = browser.version?.split('.')[0];
    }
    if (
      !userHasAllowedRecording ||
      (browserName === 'safari' && browserMainVersion < 15)
    ) {
      setIsRecordingAllowed(false);
    } else {
      setIsRecordingAllowed(true);
    }
  }, [userHasAllowedRecording]);

  useEffect(() => {
    if (browser) {
      setBrowserName(browser.name);
    }
  }, []);

  const handleRecordProcessed = useCallback(
    async (data, audioBuffer) => {
      try {
        const processedBlob = new Blob([audioBuffer ? toWav(data) : data], {
          type: 'audio/wav',
        });
        setRecordedDataBlob(processedBlob);
        const processedBlobUrl = URL.createObjectURL(processedBlob);
        setRecordedDataUrl(processedBlobUrl);
        const duration = await getAudioDuration(processedBlobUrl);
        setTotalDuration(duration);
        setTimer(maxGiideLengthInSecounds - duration);
      } catch (e) {
        handleErrorMessage(e, fallbackErrorMessage);
      }
    },
    [maxGiideLengthInSecounds]
  );

  const handleRecordProcessing = useCallback(
    async (recAudioBuffersData: AudioBuffer[], audioContext) => {
      try {
        let resultAudioBuffer: AudioBuffer;
        if (recAudioBuffersData.length > 1) {
          resultAudioBuffer = concatTwoAudioBuffers(
            recAudioBuffersData[0],
            recAudioBuffersData[1],
            audioContext
          );
        } else {
          resultAudioBuffer = recAudioBuffersData[0];
        }
        setRecAudioBuffers([resultAudioBuffer]);
        await handleRecordProcessed(resultAudioBuffer, true);
      } catch (e) {
        handleErrorMessage(e, fallbackErrorMessage);
      }
    },
    [handleRecordProcessed]
  );

  const handleStopNotSupportedConcat = useCallback(
    async (data, datablob) => {
      handleRecordProcessed(datablob, false);
    },
    [handleRecordProcessed]
  );

  const handleStopSupportedConcat = useCallback(
    async (data, datablob) => {
      try {
        const audioContext = new AudioContext();
        const audioBufferData: AudioBuffer = await getAudioBufferFromBlob(
          datablob,
          audioContext
        );
        const recAudioBuffersData: AudioBuffer[] = [
          ...recAudioBuffers,
          audioBufferData,
        ];
        await handleRecordProcessing(recAudioBuffersData, audioContext);
      } catch (e) {
        handleErrorMessage(e, fallbackErrorMessage);
      }
    },
    [handleRecordProcessing, recAudioBuffers]
  );

  const handleOnStop = useCallback(
    async (data, datablob) => {
      if (browserName === 'safari') {
        await handleStopNotSupportedConcat(data, datablob);
      } else {
        await handleStopSupportedConcat(data, datablob);
      }
      if (!clearAudioData) setClearAudioData(true);
    },
    [
      handleStopNotSupportedConcat,
      browserName,
      handleStopSupportedConcat,
      clearAudioData,
    ]
  );

  const {
    status,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    // mediaBlobUrl,
    clearBlobUrl,
    // previewAudioStream,
  } = useReactMediaRecorder({
    audio: true,
    video: false,
    onStop: handleOnStop,
    blobPropertyBag: {
      type: 'audio/wav',
    },
    askPermissionOnMount: false,
  });

  const clearRecordingState = useCallback(() => {
    stopRecording();
    clearInterval(timerInterval);
    setTimer(0);
    clearInterval(countDownInterval);
    setCountDown(3);
    clearBlobUrl();
    setAudioFileName('');
    setRecordedDataBlob(undefined);
    setRecordedDataUrl(undefined);
    setRecAudioBuffers([]);
    setBackFromPreview(false);
    setPlayerOpened(false);
    setPlaying(false);
    // setTestingMicBtnText('Test mic');
    setTestedAudio(false);
    dispatch(setOwnRecordingStarted(undefined));
    setExceededMaxDuration(false);
  }, [stopRecording, clearBlobUrl, dispatch]);

  const closeOwnRecording = useCallback(() => {
    clearRecordingState();
    setRecordingAudioState('info');
  }, [clearRecordingState]);

  const onCloseOwnRecording = useCallback(() => {
    if (recordingAudioState === 'info') {
      closeOwnRecording();
    } else {
      confirm({
        centered: true,
        closable: true,
        className: 'deleteModal closeOwnRecordingConfirmation',
        cancelText: 'Cancel',
        okText: 'Yes, delete this recording',
        onOk: () => {
          closeOwnRecording();
          if (recordingAudioState === 'recording') setExitFromRecording(true);
          return false;
        },
        title: (
          <div>
            Are you sure you want to close this recording? If so, your
            <br />
            recording will be deleted.
          </div>
        ),
      });
    }
  }, [recordingAudioState, closeOwnRecording]);

  const getDefaultRecodingName = useCallback(() => {
    if (audiopLibraryItemsCount < 9)
      return `recording0${audiopLibraryItemsCount + 1}`;
    else return `recording${audiopLibraryItemsCount + 1}`;
  }, [audiopLibraryItemsCount]);

  const handleSaveRecording = useCallback(() => {
    if (recordedDataBlob) {
      const file = new File(
        [recordedDataBlob],
        `${
          audioFileName?.length > 0 ? audioFileName : getDefaultRecodingName()
        }.wav`,
        {
          type: 'audio/wav',
        }
      );
      handleUpload(file);
      closeOwnRecording();
      setShowAudioLibrary(true);
    }
  }, [
    recordedDataBlob,
    closeOwnRecording,
    handleUpload,
    audioFileName,
    getDefaultRecodingName,
  ]);

  const handleStopRecording = useCallback(() => {
    clearInterval(timerInterval);
    stopRecording();
    setRecordingAudioState('preview');
  }, [stopRecording]);

  const handleTimerInterval = useCallback(
    continuing => {
      let time;
      if (continuing) {
        time = timer;
      } else {
        time = maxGiideLengthInSecounds;
        setTimer(maxGiideLengthInSecounds);
      }
      timerInterval = setInterval(() => {
        time = time - 0.02;
        setTimer(time);
        if (time <= 1) {
          handleStopRecording();
          setExceededMaxDuration(true);
        }
      }, 20);
    },
    [timer, handleStopRecording, maxGiideLengthInSecounds]
  );

  const handleStartRecording = useCallback(() => {
    if (!backFromPreview) {
      handleTimerInterval(false);
      setRecordingAudioState('recording');
      startRecording();
    } else {
      handleTimerInterval(true);
      startRecording();
      setBackFromPreview(false);
    }
  }, [startRecording, backFromPreview, handleTimerInterval]);

  const handlePermissionsCheckForFunction = useCallback(async callFunction => {
    const micPermissions = await gotMicPermissions();
    if (micPermissions) {
      callFunction();
    } else {
      message.warning('Please allow us to use microphone on this page first');
    }
  }, []);

  const handleCountDown = useCallback(() => {
    dispatch(setOwnRecordingStarted(true));
    if (testedAudio) {
      setTestedAudio(false);
      clearBlobUrl();
    }
    setRecordingAudioState('countDown');
    let time = countDown;
    countDownInterval = setInterval(() => {
      time -= 1;
      setCountDown(time);
      if (time === 0) {
        clearInterval(countDownInterval);
        handleStartRecording();
      }
    }, 1000);
  }, [handleStartRecording, countDown, testedAudio, clearBlobUrl, dispatch]);

  const handlePauseAudio = useCallback(() => {
    if (status === 'paused') {
      handleTimerInterval(true);
      resumeRecording();
    } else {
      clearInterval(timerInterval);
      pauseRecording();
    }
  }, [pauseRecording, resumeRecording, status, handleTimerInterval]);

  const handleDeleteRecordedAudio = useCallback(() => {
    confirm({
      centered: true,
      closable: true,
      className: 'deleteModal deleteOwnRecordingConfirmation',
      cancelText: 'Cancel',
      okText: 'Yes, delete this recording',
      onOk: () => {
        clearRecordingState();
        setRecordingAudioState('info');
        if (recordingAudioState === 'recording') setExitFromRecording(true);
        return false;
      },
      title: 'Are you sure you want to delete this recording?',
    });
  }, [clearRecordingState, recordingAudioState]);

  const handleClickBack = useCallback(() => {
    if (recordingAudioState === 'preview') {
      setRecordingAudioState('recording');
      setBackFromPreview(true);
      setPlayerOpened(false);
      setPlaying(false);
    } /*else if (recordingAudioState === 'recording') {
      setExitFromRecording(true);
      handleDeleteRecordedAudio();
    } else if (recordingAudioState === 'info') {
      closeOwnRecording();
    }*/
  }, [recordingAudioState /*, closeOwnRecording, handleDeleteRecordedAudio*/]);

  /*  const handleTestMic = useCallback(() => {
    if (testingMicBtnText === 'Test mic') {
      setTestedAudio(true);
      setTestingMicBtnText('Recording');
      startRecording();
      setTimeout(() => {
        stopRecording();
        setTestingMicBtnText('Playback');
      }, 5000);
    } else if (testingMicBtnText === 'Playback') {
      setTestAudioPlaying(true);
      const audio = new Audio(mediaBlobUrl as string);
      audio.play();
      setTimeout(() => {
        clearBlobUrl();
        setTestingMicBtnText('Test mic');
        setTestAudioPlaying(false);
      }, 5000);
    }
  }, [
    testingMicBtnText,
    mediaBlobUrl,
    startRecording,
    stopRecording,
    clearBlobUrl,
  ]);*/

  const handleClearAudioData = useCallback(() => {
    setClearAudioData(false);
    setRecordedDataBlob(undefined);
    setRecordedDataUrl(undefined);
    setRecAudioBuffers([]);
  }, []);

  useEffect(() => {
    if (clearAudioData && exitFromRecording) {
      handleClearAudioData();
      setExitFromRecording(false);
    }
  }, [handleClearAudioData, clearAudioData, exitFromRecording]);

  useEffect(() => {
    if (clearAudioData && testedAudio) handleClearAudioData();
  }, [handleClearAudioData, clearAudioData, testedAudio]);

  /*  const startRecordingTooltipMessage = useMemo<string>(() => {
    if (testingMicBtnText === 'Recording')
      return 'Test audio is being recorded, please wait';
    else if (testAudioPlaying) return 'Test audio is being played, please wait';
    else return '';
  }, [testingMicBtnText, testAudioPlaying]);*/

  const { callMutation: copySampleAudioMutation } = useMutation<
    CopySampleAudioInput,
    CopySampleAudioMutation
  >(copySampleAudio);
  const { openedStep } = useGiide(giideId);

  const handleCopySampleAudio = React.useCallback(() => {
    copySampleAudioMutation({
      giideId: giideId,
      stepId: openedStep!.id,
    });
    batch(() => {
      dispatch(
        setProcessingRecording({
          state: 'processing',
        })
      );
      dispatch(setRecordingProcessingModalVisible(true));
      dispatch(setGetStartedModalOpen('sample'));
      dispatch(setGiideTabState('transcript'));
    });
  }, [giideId, openedStep, dispatch, copySampleAudioMutation]);

  const seekTo = useCallback(
    (delta: number) => {
      const adjustedTime = calculatePlayerTimeSeek(
        delta,
        progressTime,
        totalDuration
      );
      setProgressTime(adjustedTime);
      playerRef.current?.seekTo(adjustedTime / 1000);
    },
    [progressTime, setProgressTime, playerRef, totalDuration]
  );

  const backBtnTooltipMessage = useMemo<string>(() => {
    if (exceededMaxDuration)
      return 'Cannot record more, because you exceeded max duration';
    else return '';
  }, [exceededMaxDuration]);

  const handleClickOutsideInfoTooltip = useCallback(
    event => {
      const infoTooltip = document.getElementById('infoTooltip');
      if (
        infoTooltipTextRef.current &&
        !infoTooltipTextRef.current.contains(event.target) &&
        infoTooltip &&
        !infoTooltip.contains(event.target)
      ) {
        setInfoTooltipVisible(false);
      }
    },
    [infoTooltipTextRef]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideInfoTooltip);
    return () => {
      document.removeEventListener('click', handleClickOutsideInfoTooltip);
    };
  }, [handleClickOutsideInfoTooltip]);

  const handleClickOutsideRecording = useCallback(
    event => {
      const scriptArea = document.getElementById('scriptAreaContent');
      const deleteModal = document.getElementsByClassName('deleteModal');
      if (
        recordingAudioState &&
        recordingAudioState !== 'info' &&
        ownAudioRecordingRef.current &&
        !ownAudioRecordingRef.current.contains(event.target) &&
        deleteModal.length === 0 &&
        scriptArea &&
        !scriptArea.contains(event.target)
      ) {
        setExitConfirmationModalVisible(true);
      }
    },
    [recordingAudioState, ownAudioRecordingRef]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideRecording, true);
    return () => {
      document.removeEventListener('click', handleClickOutsideRecording, true);
    };
  }, [handleClickOutsideRecording]);

  const handleBrowserVisibilityChange = useCallback(() => {
    if (
      document.visibilityState === 'hidden' &&
      status !== 'paused' &&
      recordingAudioState === 'recording'
    ) {
      clearInterval(timerInterval);
      pauseRecording();
    }
  }, [pauseRecording, status, recordingAudioState]);

  useEffect(() => {
    document.addEventListener(
      'visibilitychange',
      handleBrowserVisibilityChange
    );
    return () => {
      document.removeEventListener(
        'visibilitychange',
        handleBrowserVisibilityChange
      );
    };
  }, [handleBrowserVisibilityChange]);

  const onwRecordingContent = useCallback(() => {
    return (
      <div
        id="ownAudioRecording"
        ref={ownAudioRecordingRef}
        className={`onwRecordingWrapper ${styles.onwRecordingWrapper}`}
      >
        <Modal
          className="deleteModal exitOwnRecordingConfirmation"
          destroyOnClose
          centered
          closable
          open={exitConfirmationModalVisible}
          onCancel={() => setExitConfirmationModalVisible(false)}
          footer={
            <div className="flexRight">
              <Button onClick={() => setExitConfirmationModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  closeOwnRecording();
                  if (recordingAudioState === 'recording')
                    setExitFromRecording(true);
                  setExitConfirmationModalVisible(false);
                }}
              >
                Yes, delete this recording
              </Button>
            </div>
          }
        >
          <div className="deleteConfirmationText">
            Are you sure you want to leave this recording? If so, your
            <br />
            recording will be deleted.
          </div>
        </Modal>

        {recordingAudioState !== 'countDown' && (
          <>
            {recordingAudioState === 'preview' && browserName !== 'safari' && (
              <Tooltip title={backBtnTooltipMessage}>
                <img
                  className={`${styles.onwRecordingBackBtn} ${
                    exceededMaxDuration
                      ? styles.onwRecordingBackBtnDisabled
                      : ''
                  }`}
                  src={backIcon}
                  alt="back icon"
                  onClick={() => {
                    if (!exceededMaxDuration) handleClickBack();
                  }}
                />
              </Tooltip>
            )}
            <img
              className={styles.recordingCloseIcon}
              src={closeIcon}
              alt="close icon"
              onClick={onCloseOwnRecording}
            />
          </>
        )}

        {recordingAudioState === 'preview' ? (
          <div className={styles.finishContent}>
            <h2>Preview</h2>
            <div className={styles.recordedAudioPlayerSpaceWrapper}>
              {playerOpened ? (
                <div className={styles.recordedAudioPlayerWrapper}>
                  <div
                    className={`recordedAudioPlayer ${styles.recordedAudioPlayer}`}
                  >
                    <Slider
                      value={progressTime}
                      onChange={value => {
                        setProgressTime(value);
                        playerRef.current?.seekTo(value / 1000, 'seconds');
                      }}
                      max={Math.round(totalDuration * 1000)}
                      step={1}
                    />
                    <div className={styles.recordedAudioPlayerTimes}>
                      <div>{formatSeconds(progressTime / 1000)}</div>
                      <div>{formatSeconds(totalDuration)}</div>
                    </div>
                    <div className={styles.recordedAudioPlayerControls}>
                      <img
                        src={backwardIcon}
                        alt="backward icon"
                        onClick={() => seekTo(-15000)}
                      />
                      <div className={styles.playerPlayIcon}>
                        <img
                          src={playing ? pauseIcon : playIcon}
                          alt="player play icon"
                          onClick={() => setPlaying(!playing)}
                        />
                      </div>
                      <img
                        src={forwardIcon}
                        alt="forward icon"
                        onClick={() => seekTo(15000)}
                      />
                    </div>
                    <ReactPlayer
                      ref={playerRef}
                      url={recordedDataUrl as string}
                      playing={playing}
                      progressInterval={20}
                      onProgress={({ playedSeconds }) =>
                        setProgressTime(playedSeconds * 1000)
                      }
                      onEnded={() => {
                        playerRef.current?.seekTo(0);
                        setPlaying(false);
                      }}
                      width={0}
                      height={0}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.openPlayerBtnWrapper}>
                  <div
                    className={styles.playRecordingBtnOutline}
                    onClick={() => {
                      setProgressTime(0);
                      setPlayerOpened(true);
                      setPlaying(true);
                    }}
                  >
                    <img
                      className={styles.playInPlayerIcon}
                      src={playInPlayerIcon}
                      alt="play icon"
                    />
                  </div>
                  <p>{formatSeconds(totalDuration)}</p>
                </div>
              )}
            </div>
            <Input
              className={styles.recordNameInput}
              placeholder="Name your file"
              onChange={handleInputChange(setAudioFileName)}
              value={audioFileName}
              maxLength={50}
              autoFocus
            />
            <Flex gap={'middle'} style={{ marginTop: '8px' }}>
              <Button
                type={'primary'}
                danger={true}
                onClick={handleDeleteRecordedAudio}
              >
                Delete
              </Button>
              <Button type={'primary'} onClick={handleSaveRecording}>
                Save
              </Button>
            </Flex>
          </div>
        ) : recordingAudioState === 'countDown' ? (
          <div className={styles.onwRecordingCountDown}>
            <div className={styles.countDownCircle}>
              <div className={styles.countDownText}>{countDown}</div>
            </div>
          </div>
        ) : recordingAudioState === 'recording' ? (
          <div className={styles.recordingContent}>
            <h2>
              {status === 'paused' || backFromPreview
                ? 'Recording paused'
                : 'Recording'}
            </h2>
            <h1>{formatSeconds(timer)}</h1>
            {/* <div className={styles.recordingAudioPreviewWrapper}>
              <div />
            </div> */}
            <div className={styles.recordingControlButtons}>
              {(status === 'paused' || backFromPreview) && (
                <div className={styles.deleteRecordingBtnWrapper}>
                  <div
                    className={styles.deleteRecordingBtnOutline}
                    onClick={handleDeleteRecordedAudio}
                  >
                    <img
                      className={styles.deleteIcon}
                      src={deleteIcon}
                      alt="delete icon"
                    />
                  </div>
                  <div className={styles.deleteRecordingBtnText}>Delete</div>
                </div>
              )}
              <div className={styles.controlRecordingBtnWrapper}>
                <div
                  className={`${styles.stopRecordingBtnOutline} ${styles.stopRecordingBtnOutlinePaused}`}
                  onClick={
                    !backFromPreview ? handlePauseAudio : handleStartRecording
                  }
                >
                  {status === 'paused' || backFromPreview ? (
                    <div className={styles.stopRecordingBtnInline} />
                  ) : (
                    <div className={styles.stopRecordingBtnInline}>
                      <img
                        className={styles.pauseRecordingIcon}
                        src={pauseRecodingIcon}
                        alt="pause icon"
                      />
                    </div>
                  )}
                </div>
                <div className={styles.stopRecordingBtnText}>
                  {status === 'paused' || backFromPreview ? 'Resume' : 'Pause'}
                </div>
              </div>
              {(status === 'paused' || backFromPreview) && (
                <div className={styles.keepRecordingBtnWrapper}>
                  <div
                    className={styles.keepRecordingBtnOutline}
                    onClick={handleStopRecording}
                  >
                    <img
                      className={styles.keepIcon}
                      src={keepIcon}
                      alt="keep icon"
                    />
                  </div>
                  <div className={styles.keepRecordingBtnText}>Keep</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.infoWrapper}>
            <Modal
              className="recordingHelpModal"
              destroyOnClose
              centered
              closable
              open={infoTooltipVisible}
              onCancel={() => setInfoTooltipVisible(!infoTooltipVisible)}
              footer={null}
            >
              <div className={styles.toolTipModal}>
                {/* <div className={styles.modalPlayer}>
                  <iframe
                    title="Giide Web Player"
                    src={
                      'https://webv5.giide.com/giide/giide-9c1ddec7-81ee-4e25-8e50-cc6d7ebc0c99'
                    }
                    frameBorder="0"
                    // style={{ width: '100%', height: '100%' }}
                  />
                </div> */}
                {tooltipText()}
              </div>
            </Modal>
            <div className={styles.micWrapper}>
              <img src={microphone} alt="microphone" />
            </div>
            {isRecordingAllowed ? (
              <div className={styles.infoContent}>
                <h2>
                  Get ready to record up to {maxGiideLengthInSecounds / 60}{' '}
                  minutes of audio!
                </h2>
                <div className={styles.testMicWrapper}>
                  {/*              <Button
                    className={styles.testMicBtn}
                    onClick={() => handlePermissionsCheckForFunction(handleTestMic)}
                    disabled={testAudioPlaying || testingMicBtnText === 'Recording'}
                  >
                    {testingMicBtnText}
                  </Button>*/}
                  <div className={styles.testMicText}>
                    Recording in your browser is simple—just allow access your
                    system microphone when prompted.
                    {/* If you’re feeling
                    tongue-tied,
                    <span
                      ref={infoTooltipTextRef}
                      className={styles.recordingTips}
                      onClick={() => setInfoTooltipVisible(!infoTooltipVisible)}
                    >
                      {' '}
                      click here{' '}
                    </span>
                    for some tips and tricks to get you going. */}
                  </div>
                </div>
                {/*<Tooltip title={startRecordingTooltipMessage}>*/}
                <Button
                  className={styles.startRecordingButton}
                  type={'primary'}
                  size={'large'}
                  onClick={() =>
                    handlePermissionsCheckForFunction(handleCountDown)
                  }
                  // disabled={!!startRecordingTooltipMessage}
                >
                  {isRecordingAllowed ? 'Start Recording' : 'Allow Recording'}
                </Button>
                {
                  <div className={styles.sampleAudioText}>
                    Not sure where to start?
                    <span
                      className={styles.recordingTips}
                      onClick={() => handleCopySampleAudio()}
                    >
                      {' '}
                      Try our sample audio!{' '}
                    </span>
                  </div>
                }
                {/*</Tooltip>*/}
              </div>
            ) : (
              <div className={styles.infoContent}>
                <h2>
                  It looks like your browser doesn't
                  <br /> support recording.
                </h2>
                <div className={styles.testMicText}>
                  Try updating your browser, or using Chrome or Firefox.
                  <br />
                  You can still upload your own pre-recorded audio by clicking
                  the 'Upload Audio' tab above.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [
    audioFileName,
    onCloseOwnRecording,
    handleDeleteRecordedAudio,
    handlePauseAudio,
    handleSaveRecording,
    handleStartRecording,
    handleStopRecording,
    handleCopySampleAudio,
    recordingAudioState,
    status,
    timer,
    handleClickBack,
    recordedDataUrl,
    // handleTestMic,
    // testingMicBtnText,
    // testAudioPlaying,
    handleCountDown,
    countDown,
    playerRef,
    playing,
    progressTime,
    seekTo,
    backFromPreview,
    handlePermissionsCheckForFunction,
    playerOpened,
    maxGiideLengthInSecounds,
    exceededMaxDuration,
    backBtnTooltipMessage,
    // startRecordingTooltipMessage,
    infoTooltipVisible,
    totalDuration,
    browserName,
    closeOwnRecording,
    exitConfirmationModalVisible,
    isRecordingAllowed,
  ]);

  return {
    onwRecordingContent,
    recordingAudioState,
    setRecordingAudioState,
    showAudioLibrary,
    setShowAudioLibrary,
  };
};

export default useRecordingOwnAudio;
